/**
 * 🟥 CUADRO NEGRO V6 — BranchPicker.
 *
 * Dropdown compacto que muestra el branch actual y permite:
 *   1) Cambiar a otro branch (POST /api/github/set-branch).
 *   2) Crear un nuevo branch desde HEAD (POST /api/github/create-branch).
 *
 * Renderiza solo si `githubSource.owner/repo` están presentes.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { GitBranch, Plus, Check, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function BranchPicker({
  moduleId,
  githubSource,
  isEs = true,
  onBranchChange,
}) {
  const [open, setOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(null); // name del branch en spinner
  const [creating, setCreating] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const wrapRef = useRef(null);

  const currentBranch = githubSource?.branch || 'main';
  const repoLabel = githubSource ? `${githubSource.owner}/${githubSource.repo}` : '';

  const fetchBranches = async () => {
    if (!moduleId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/github/branches`, {
        params: { module_id: moduleId },
      });
      setBranches(res.data?.branches || []);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(isEs ? `No se pudieron cargar branches: ${detail}` : `Branches load failed: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(() => { fetchBranches(); }, 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, moduleId]);

  // Cerrar al hacer click fuera.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setShowCreate(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const handleSetBranch = async (branchName) => {
    if (branchName === currentBranch) {
      setOpen(false);
      return;
    }
    setSwitching(branchName);
    try {
      const res = await axios.post(`${API}/github/set-branch`, {
        module_id: moduleId,
        branch: branchName,
      });
      toast.success(
        isEs ? `Branch cambiado a ${res.data?.branch}` : `Switched to ${res.data?.branch}`
      );
      onBranchChange?.(res.data?.branch || branchName);
      setOpen(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(isEs ? `Cambio de branch falló: ${detail}` : `Branch switch failed: ${detail}`);
    } finally {
      setSwitching(null);
    }
  };

  const handleCreateBranch = async () => {
    const name = (newBranchName || '').trim();
    if (!name) {
      toast.error(isEs ? 'Nombre requerido' : 'Name required');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/github/create-branch`, {
        module_id: moduleId,
        new_branch: name,
        switch_to_new: true,
      });
      toast.success(
        isEs ? `Branch "${res.data?.branch}" creado y activo` : `Branch "${res.data?.branch}" created`
      );
      onBranchChange?.(res.data?.branch || name);
      setNewBranchName('');
      setShowCreate(false);
      setOpen(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      if (detail === 'branch_already_exists' || detail === 'branch_create_failed_422') {
        toast.error(isEs ? 'Ese branch ya existe' : 'Branch already exists');
      } else if (detail === 'invalid_branch_name') {
        toast.error(isEs ? 'Nombre inválido (usa a-z, 0-9, /, -, _)' : 'Invalid name');
      } else {
        toast.error(isEs ? `No se pudo crear: ${detail}` : `Create failed: ${detail}`);
      }
    } finally {
      setCreating(false);
    }
  };

  if (!githubSource) return null;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md bg-violet-500/10 text-violet-300 hover:bg-violet-500/20 transition-colors"
        data-testid="branch-picker-trigger"
        title={isEs ? `Branch actual: ${currentBranch}` : `Current branch: ${currentBranch}`}
      >
        <GitBranch className="w-3.5 h-3.5" />
        <span className="font-mono truncate max-w-[140px]">{currentBranch}</span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-1 w-72 bg-slate-900 border border-slate-700 rounded-lg shadow-2xl z-50 p-1"
          data-testid="branch-picker-panel"
        >
          <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800 mb-1 truncate">
            {repoLabel}
          </div>
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-6 text-slate-400 text-xs">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : branches.length === 0 ? (
              <div className="py-3 text-center text-slate-500 text-xs">
                {isEs ? 'Sin branches' : 'No branches'}
              </div>
            ) : (
              branches.map((b) => (
                <button
                  key={b.name}
                  type="button"
                  onClick={() => handleSetBranch(b.name)}
                  disabled={switching === b.name}
                  data-testid={`branch-item-${b.name}`}
                  className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 text-xs rounded-md transition-colors ${
                    b.name === currentBranch
                      ? 'bg-violet-500/20 text-violet-200'
                      : 'text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  <span className="flex items-center gap-1.5 truncate">
                    <GitBranch className="w-3 h-3 opacity-70 shrink-0" />
                    <span className="font-mono truncate">{b.name}</span>
                    {b.is_default && (
                      <span className="text-[9px] uppercase px-1 rounded bg-slate-700 text-slate-400">
                        {isEs ? 'default' : 'default'}
                      </span>
                    )}
                  </span>
                  {switching === b.name ? (
                    <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                  ) : b.name === currentBranch ? (
                    <Check className="w-3 h-3 text-violet-300 shrink-0" />
                  ) : null}
                </button>
              ))
            )}
          </div>
          <div className="border-t border-slate-800 mt-1 pt-1">
            {showCreate ? (
              <div className="p-2 space-y-2">
                <input
                  type="text"
                  value={newBranchName}
                  onChange={(e) => setNewBranchName(e.target.value)}
                  placeholder={isEs ? 'feature/nombre' : 'feature/name'}
                  className="w-full px-2 py-1 text-xs bg-slate-950 border border-slate-700 rounded text-slate-200 font-mono focus:border-violet-500 focus:outline-none"
                  data-testid="branch-create-input"
                  disabled={creating}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateBranch();
                    if (e.key === 'Escape') setShowCreate(false);
                  }}
                />
                <div className="text-[10px] text-slate-500">
                  {isEs ? `Desde HEAD de ${currentBranch}` : `From HEAD of ${currentBranch}`}
                </div>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={handleCreateBranch}
                    disabled={creating}
                    data-testid="branch-create-confirm"
                    className="flex-1 px-2 py-1 text-xs rounded-md bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-60 flex items-center justify-center gap-1"
                  >
                    {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    {isEs ? 'Crear' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowCreate(false); setNewBranchName(''); }}
                    disabled={creating}
                    className="px-2 py-1 text-xs rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowCreate(true)}
                data-testid="branch-create-open"
                className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs rounded-md text-emerald-300 hover:bg-emerald-500/10"
              >
                <Plus className="w-3.5 h-3.5" /> {isEs ? 'Crear nuevo branch' : 'Create new branch'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
